import { AxiosResponse } from 'axios';

import apiClient from './client';
import i18n from 'i18n';
import { ApiResponseDto } from 'interfaces/apiResponse';
import {
  CreateErganiSettings,
  ErganiBranches,
  ErganiEmployerInfo,
  ErganiScheduleOverrideResponse,
  ErganiScheduleCheckResponse,
  ErganiScheduleDeleteResponse,
  ErganiWeeklyScheduleCheckResponse,
  ErganiWeeklyScheduleResponse,
} from 'interfaces/ergani';

interface ErganiService {
  getUsername(): Promise<string | null>;
  upsertCredentials(postData: CreateErganiSettings): Promise<void>;
  getEmployer(): Promise<ErganiEmployerInfo>;
  getBranches(): Promise<ErganiBranches>;
  sendScheduleOverride(overrideId: string): Promise<ErganiScheduleOverrideResponse>;
  checkScheduleOverride(overrideId: string): Promise<ErganiScheduleCheckResponse>;
  deleteScheduleOverride(overrideId: string): Promise<ErganiScheduleDeleteResponse>;
  checkWeeklySchedule(scheduleId: number): Promise<ErganiWeeklyScheduleCheckResponse>;
  sendWeeklySchedule(scheduleId: number): Promise<ErganiWeeklyScheduleResponse>;
}

const endpoints = {
  getUsername: '/ergani/authenticate/username',
  upsertCredentials: '/ergani/authenticate',
  employer: '/ergani/employer',
  branches: '/ergani/branches',
  scheduleOverride: (id: string) => `/ergani/schedule/overrides/${id}`,
  scheduleOverrideCheck: (id: string) => `/ergani/schedule/overrides/${id}/check`,
  weeklySchedule: (id: number) => `/ergani/schedule/${id}`,
  weeklyScheduleCheck: (id: number) => `/ergani/schedule/${id}/check`,
};

export const erganiServices: ErganiService = {
  getUsername: async (): Promise<string | null> => {
    try {
      const response: AxiosResponse<ApiResponseDto<string>> = await apiClient.get(endpoints.getUsername);

      if (response.data.errors) {
        throw new Error(response.data.errors!.map(error => error.message).join(', '));
      }

      return response.data.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
  upsertCredentials: async (postData: CreateErganiSettings): Promise<void> => {
    try {
      const response: AxiosResponse<ApiResponseDto<void>> = await apiClient.put(endpoints.upsertCredentials, postData);

      if (response.data.errors) {
        throw new Error(response.data.errors.map(error => error.message).join(', '));
      }
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(i18n.t('apiErrors.erganiInvalidCredentials'));
      }

      if (error.response?.status === 502) {
        throw new Error(i18n.t('apiErrors.erganiUnavailable'));
      }

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors[0].message);
      }

      throw error;
    }
  },

  getEmployer: async (): Promise<ErganiEmployerInfo> => {
    const response: AxiosResponse<ApiResponseDto<ErganiEmployerInfo>> = await apiClient.get(endpoints.employer);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  getBranches: async (): Promise<ErganiBranches> => {
    const response: AxiosResponse<ApiResponseDto<ErganiBranches>> = await apiClient.get(endpoints.branches);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  sendScheduleOverride: async (overrideId: string): Promise<ErganiScheduleOverrideResponse> => {
    const response: AxiosResponse<ApiResponseDto<ErganiScheduleOverrideResponse>> = await apiClient.post(
      endpoints.scheduleOverride(overrideId),
    );

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },
  checkScheduleOverride: async (overrideId: string): Promise<ErganiScheduleCheckResponse> => {
    const response: AxiosResponse<ApiResponseDto<ErganiScheduleCheckResponse>> = await apiClient.get(
      endpoints.scheduleOverrideCheck(overrideId),
    );

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  deleteScheduleOverride: async (overrideId: string): Promise<ErganiScheduleDeleteResponse> => {
    const response: AxiosResponse<ApiResponseDto<ErganiScheduleDeleteResponse>> = await apiClient.delete(
      endpoints.scheduleOverride(overrideId),
    );

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  checkWeeklySchedule: async (scheduleId: number): Promise<ErganiWeeklyScheduleCheckResponse> => {
    const response: AxiosResponse<ApiResponseDto<ErganiWeeklyScheduleCheckResponse>> = await apiClient.get(
      endpoints.weeklyScheduleCheck(scheduleId),
    );

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  sendWeeklySchedule: async (scheduleId: number): Promise<ErganiWeeklyScheduleResponse> => {
    const response: AxiosResponse<ApiResponseDto<ErganiWeeklyScheduleResponse>> = await apiClient.post(
      endpoints.weeklySchedule(scheduleId),
    );

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },
};
